import { useState } from "react";
import {
  PhoneCall,
  Megaphone,
  Users,
  Workflow,
  BarChart3,
  ArrowUpRight,
} from "lucide-react";
import Waveform from "@/components/Waveform";

const TABS = [
  { id: "voice", label: "Voice Agents", icon: PhoneCall },
  { id: "marketing", label: "Marketing Studio", icon: Megaphone },
  { id: "crm", label: "CRM Leads", icon: Users },
  { id: "automations", label: "Automations", icon: Workflow },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
];

const AGENTS = [
  { name: "Front Desk Agent", voice: "Warm, conversational", calls: 128, status: "Active" },
  { name: "Sales Qualifier", voice: "Confident, upbeat", calls: 86, status: "Active" },
  { name: "Support Triage", voice: "Calm, reassuring", calls: 54, status: "Paused" },
];

const CAMPAIGNS = [
  { name: "Spring Re-engagement", channel: "Email", reach: "4,200 contacts", status: "Draft" },
  { name: "Missed Call Follow-up", channel: "SMS", reach: "310 contacts", status: "Scheduled" },
  { name: "New Offer Launch", channel: "Paid Social", reach: "12,000 impressions", status: "Draft" },
];

const LEADS = [
  { name: "Amelia Hart", company: "Hart & Co", stage: "Qualified", score: 92 },
  { name: "Daniel Osei", company: "Osei Logistics", stage: "Call Booked", score: 87 },
  { name: "Priya Nair", company: "Nair Wellness", stage: "New", score: 74 },
  { name: "Tom Becker", company: "Becker Dental", stage: "Nurturing", score: 61 },
];

const AUTOMATIONS = [
  { name: "Missed call → instant SMS", runs: 342, state: "On" },
  { name: "New lead → welcome email series", runs: 218, state: "On" },
  { name: "No-show → rebooking voice call", runs: 57, state: "Off" },
];

const WEEK = [
  { day: "Mon", v: 42 }, { day: "Tue", v: 68 }, { day: "Wed", v: 55 },
  { day: "Thu", v: 84 }, { day: "Fri", v: 96 }, { day: "Sat", v: 38 }, { day: "Sun", v: 24 },
];

const Badge = ({ children }) => (
  <span className="rounded-full border border-neutral-200 bg-neutral-50 px-2.5 py-0.5 text-[11px] font-semibold text-neutral-500">
    {children}
  </span>
);

const SampleNote = () => (
  <div className="flex items-center gap-2 rounded-xl border border-dashed border-neutral-300 bg-neutral-50 px-4 py-2.5">
    <span className="h-1.5 w-1.5 rounded-full bg-neutral-400" />
    <p className="text-xs font-medium text-neutral-500">
      Sample data for design preview only — nothing here is live.
    </p>
  </div>
);

function VoicePanel() {
  return (
    <div className="space-y-4">
      {AGENTS.map((a) => (
        <div key={a.name} className="flex flex-col gap-4 rounded-2xl border border-neutral-200 bg-white p-5 transition-shadow hover:shadow-md sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#111111] text-white">
              <PhoneCall size={18} />
            </span>
            <div>
              <p className="text-sm font-semibold text-[#111111]">{a.name}</p>
              <p className="text-xs text-neutral-500">{a.voice}</p>
            </div>
          </div>
          <div className="flex items-center gap-5">
            <div className="hidden w-28 sm:block"><Waveform compact /></div>
            <div className="text-right">
              <p className="text-lg font-bold text-[#111111]">{a.calls}</p>
              <p className="text-[11px] text-neutral-400">calls today</p>
            </div>
            <Badge>{a.status}</Badge>
          </div>
        </div>
      ))}
    </div>
  );
}

function MarketingPanel() {
  return (
    <div className="grid gap-4 sm:grid-cols-3">
      {CAMPAIGNS.map((c) => (
        <div key={c.name} className="rounded-2xl border border-neutral-200 bg-white p-5 transition-shadow hover:shadow-md">
          <div className="mb-4 flex items-center justify-between">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-neutral-100 text-[#111111]">
              <Megaphone size={17} />
            </span>
            <Badge>{c.status}</Badge>
          </div>
          <p className="text-sm font-semibold text-[#111111]">{c.name}</p>
          <p className="mt-1 text-xs text-neutral-500">{c.channel} · {c.reach}</p>
          <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-neutral-100">
            <div className="h-full w-1/3 rounded-full bg-[#111111]" />
          </div>
        </div>
      ))}
    </div>
  );
}

function CrmPanel() {
  return (
    <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white">
      <table className="w-full text-left text-sm" data-testid="crm-sample-table">
        <thead>
          <tr className="border-b border-neutral-200 bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
            <th className="px-5 py-3 font-semibold">Lead</th>
            <th className="hidden px-5 py-3 font-semibold sm:table-cell">Company</th>
            <th className="px-5 py-3 font-semibold">Stage</th>
            <th className="px-5 py-3 text-right font-semibold">Score</th>
          </tr>
        </thead>
        <tbody>
          {LEADS.map((l) => (
            <tr key={l.name} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50/60">
              <td className="px-5 py-3.5 font-medium text-[#111111]">{l.name}</td>
              <td className="hidden px-5 py-3.5 text-neutral-500 sm:table-cell">{l.company}</td>
              <td className="px-5 py-3.5"><Badge>{l.stage}</Badge></td>
              <td className="px-5 py-3.5 text-right font-semibold text-[#111111]">{l.score}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AutomationsPanel() {
  return (
    <div className="space-y-4">
      {AUTOMATIONS.map((a) => (
        <div key={a.name} className="flex items-center justify-between rounded-2xl border border-neutral-200 bg-white p-5 transition-shadow hover:shadow-md">
          <div className="flex items-center gap-4">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-neutral-100 text-[#111111]">
              <Workflow size={18} />
            </span>
            <div>
              <p className="text-sm font-semibold text-[#111111]">{a.name}</p>
              <p className="text-xs text-neutral-500">{a.runs} runs this month</p>
            </div>
          </div>
          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${a.state === "On" ? "bg-[#111111] text-white" : "bg-neutral-100 text-neutral-500"}`}>
            {a.state}
          </span>
        </div>
      ))}
    </div>
  );
}

function AnalyticsPanel() {
  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-3">
        {[
          { label: "Calls handled", value: "407" },
          { label: "Leads captured", value: "86" },
          { label: "Campaign reach", value: "16.5k" },
        ].map((s) => (
          <div key={s.label} className="rounded-2xl border border-neutral-200 bg-white p-5">
            <p className="text-xs font-medium text-neutral-500">{s.label}</p>
            <p className="mt-2 flex items-center gap-1.5 text-2xl font-bold text-[#111111]">
              {s.value}
              <ArrowUpRight size={16} className="text-neutral-400" />
            </p>
          </div>
        ))}
      </div>
      <div className="rounded-2xl border border-neutral-200 bg-white p-5 sm:p-6" data-testid="analytics-sample-chart">
        <p className="mb-5 text-xs font-semibold uppercase tracking-wider text-neutral-500">
          Calls this week
        </p>
        <div className="flex h-36 items-end gap-2 sm:gap-3">
          {WEEK.map((d) => (
            <div key={d.day} className="flex flex-1 flex-col items-center gap-2">
              <div
                className="w-full rounded-t-lg bg-[#111111] transition-all hover:bg-neutral-600"
                style={{ height: `${d.v}%`, opacity: 0.35 + (d.v / 100) * 0.65 }}
                title={`${d.day}: ${d.v} calls`}
              />
              <span className="text-[10px] font-medium text-neutral-400">{d.day}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const PANELS = {
  voice: VoicePanel,
  marketing: MarketingPanel,
  crm: CrmPanel,
  automations: AutomationsPanel,
  analytics: AnalyticsPanel,
};

export default function ProductPreview() {
  const [active, setActive] = useState("voice");
  const Panel = PANELS[active];

  return (
    <section id="product" data-testid="product-preview-section" className="border-t border-neutral-100 bg-neutral-50/60 py-20 sm:py-28">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-400">Product preview</p>
          <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-[#111111] sm:text-5xl">
            Everything in one dashboard
          </h2>
          <p className="mt-5 text-base leading-relaxed text-neutral-500 sm:text-lg">
            Voice agents, campaigns, leads, automations and analytics — explore
            the interactive preview below.
          </p>
        </div>

        <div className="mt-12 rounded-3xl border border-neutral-200 bg-white p-3 shadow-[0_32px_80px_-32px_rgba(0,0,0,0.15)] sm:p-5">
          <div
            role="tablist"
            aria-label="Product areas"
            data-testid="dashboard-tabs"
            className="flex flex-wrap gap-1.5 rounded-2xl bg-neutral-100 p-1.5"
          >
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const selected = active === tab.id;
              return (
                <button
                  key={tab.id}
                  role="tab"
                  aria-selected={selected}
                  data-testid={`tab-${tab.id}`}
                  onClick={() => setActive(tab.id)}
                  className={`flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-xl px-3 py-2.5 text-xs font-semibold transition-all sm:text-sm ${
                    selected
                      ? "bg-white text-[#111111] shadow-sm"
                      : "text-neutral-500 hover:text-[#111111]"
                  }`}
                >
                  <Icon size={15} />
                  <span className="hidden min-[420px]:inline">{tab.label}</span>
                </button>
              );
            })}
          </div>

          <div
            role="tabpanel"
            data-testid={`panel-${active}`}
            className="mt-4 rounded-2xl border border-neutral-100 bg-neutral-50/50 p-4 sm:p-6"
          >
            <div className="mb-4"><SampleNote /></div>
            <Panel />
          </div>
        </div>
      </div>
    </section>
  );
}
